import { DefaultCategoryOutput } from 'dtos/diligence/DiligenceOutput';
import { AdminSaveInput, RequiredCategoryInput } from 'dtos/diligence/AdminSaveInput';
import { makeAutoObservable, runInAction } from 'mobx';
import { AuthStore } from 'stores/users/AuthStore';
import { LoadStatus } from 'utils/types';
import { serverUri } from 'utils/config';

export class CancelledEvent extends Event {
  static eventType = 'closed';
  constructor() {
    super(CancelledEvent.eventType);
  }
}

export class SavedEvent extends Event {
  static eventType = 'saved';
  categories: RequiredCategoryInput[];
  constructor(categories: RequiredCategoryInput[]) {
    super(SavedEvent.eventType);
    this.categories = categories;
  }
}

export type JobCategory = {
  title: string;
  ask: string;
};

export type AdminCategory = {
  key: string;
  title: string;
  ask: string;
  required: boolean;
};

export class DiligenceAdminStore {
  readonly events = new EventTarget();
  closing = false;
  saveStatus: LoadStatus = 'none';
  categories: AdminCategory[] = [];
  customCategories: AdminCategory[] = [];
  prevCategories: AdminCategory[] = [];
  prevCustomCategories: AdminCategory[] = [];
  lastKey = 0;

  constructor(
    private readonly token: string,
    private readonly authStore: AuthStore,
    private readonly defaultCategories: DefaultCategoryOutput[],
    jobCategories: JobCategory[]
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.categories = defaultCategories.map((defaultCategory): AdminCategory => {
      const jobCategory = jobCategories.find((jobCategory) => jobCategory.title === defaultCategory.title);
      const defaultAsk = defaultCategory.ask || '';
      return {
        key: `${++this.lastKey}`,
        title: defaultCategory.title,
        ask: jobCategory ? jobCategory.ask : defaultAsk,
        required: jobCategory ? true : false,
      };
    });
    this.customCategories = jobCategories
      .filter(
        (jobCategory) => !defaultCategories.find((defaultCategory) => defaultCategory.title === jobCategory.title)
      )
      .map((jobCategory): AdminCategory => {
        return {
          key: `${++this.lastKey}`,
          title: jobCategory.title,
          ask: jobCategory.ask,
          required: true,
        };
      });
    this.prevCategories = this.categories.map((category) => ({ ...category }));
    this.prevCustomCategories = this.customCategories.map((category) => ({ ...category }));
    this.saveStatus = 'none';
    this.closing = false;
  }

  startClosing = () => {
    this.closing = true;
  };

  handleClosed = () => {
    this.closeCallback();
  };

  private closeCallback = () => {
    this.events.dispatchEvent(new CancelledEvent());
  };

  toggleCategory = (category: AdminCategory) => {
    category.required = !category.required;
  };

  setCategoryTitle = (category: AdminCategory, title: string) => {
    category.title = title;
  };

  setCategoryAsk = (category: AdminCategory, ask: string) => {
    category.ask = ask;
  };

  addCustomCategory = () => {
    this.customCategories.push({
      key: `${++this.lastKey}`,
      title: '',
      ask: '',
      required: true,
    });
  };

  deleteCustomCategory = (category: AdminCategory) => {
    this.customCategories = this.customCategories.filter((customCategory) => customCategory.key !== category.key);
  };

  get usingRecommended(): boolean {
    if (this.customCategories.length > 0) return false;
    return this.defaultCategories.every((defaultCategory, index) => {
      return (
        this.categories[index].ask === (defaultCategory.ask || '') &&
        this.categories[index].required === !!defaultCategory.recommended
      );
    });
  }

  toggleRecommended = () => {
    if (this.usingRecommended) {
      // restore backed up categories
      this.categories = this.prevCategories.map((category) => ({ ...category }));
      this.customCategories = this.prevCustomCategories.map((category) => ({ ...category }));
    } else {
      // back up categories
      this.prevCategories = this.categories.map((category) => ({ ...category }));
      this.prevCustomCategories = this.customCategories.map((category) => ({ ...category }));
      // use recommended
      this.categories = this.categories.map((category, index) => ({
        ...category,
        ask: this.defaultCategories[index].ask || '',
        required: !!this.defaultCategories[index].recommended,
      }));
      this.customCategories = [];
    }
  };

  get requiredCategories(): RequiredCategoryInput[] {
    return [...this.categories, ...this.customCategories]
      .filter((adminCategory) => adminCategory.required)
      .map((adminCategory) => ({
        title: adminCategory.title,
        ask: adminCategory.ask,
      }));
  }

  get valid() {
    return this.customCategories.every((category) => !!category.title);
  }

  saveModal = async () => {
    if (!this.valid) return;
    this.saveStatus = 'loading';
    const dto: AdminSaveInput = {
      categories: this.requiredCategories,
    };
    try {
      const response = await this.authStore.fetchWithAuth(
        `${serverUri}/diligence/${encodeURIComponent(this.token)}/job`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dto),
        }
      );
      if (!response.ok) {
        throw new Error();
      }
      runInAction(() => {
        this.saveStatus = 'success';
        this.closeCallback = () => {
          this.events.dispatchEvent(new SavedEvent(this.requiredCategories));
        };
      });
      this.startClosing();
    } catch (err) {
      console.error(err);
      runInAction(() => {
        this.saveStatus = 'error';
      });
    }
  };
}
